import React, { useState, useEffect, useContext } from "react";
import useAxios from "../../utils/useAxios";
import { jwtDecode } from "jwt-decode";

import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import CssBaseline from "@mui/material/CssBaseline";
import MuiAppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import List from "@mui/material/List";

import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import theme from "../../Theme";
import MailIcon from "@mui/icons-material/Mail";
import Badge from "@mui/material/Badge";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import DeleteIcon from "@mui/icons-material/Delete";
import SettingsIcon from "@mui/icons-material/Settings";
import Tooltip from "@mui/material/Tooltip";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import logo from "../../components/images/FS1.png";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
  Avatar,
  Button,
  Checkbox,
  ListItemAvatar,
  Menu,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import AuthContext from "../../context/AuthContext";
import {
  ArrowBack,
  Person,
  PersonAdd,
  Search as SearchIcon,
  FactCheckOutlined,
  WarningAmberRounded,
} from "@mui/icons-material";
import { motion } from "framer-motion";

import {
  Home,
  Description,
  ViewList,
  Assignment,
  Category,
  Assessment,
  Settings,
  Logout,
} from "@mui/icons-material";
import {
  NavLink,
  Link,
  useLocation,
  useParams,
  useNavigate,
} from "react-router-dom";
import BasicBreadcrumbs from "./BreadCrumbs";
import HeaderNavLinks from "./HeaderNavLinks";
import HeaderMobileMenu from "./HeaderMobileMenu";
import WebSocketNotification from "../WebSocketNotification";
import AppLauncherButton from "../Assets/AppLauncherIcon";
import { buildContextualLogisticsPath } from "../../features/Logistics/utils/logisticsNavigationContext";
import DesignServicesIcon from '@mui/icons-material/DesignServices';
import { useBreadcrumbs } from "../../context/BreadcrumbContext";

const drawerWidth = 240;

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 500,
  bgcolor: "background.paper",
  borderRadius: 3,
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflowY: "auto",
};

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme }) => ({
  transition: theme.transitions.create(["margin", "width"], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,

  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {

        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(["margin", "width"], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

function ChildModal() {
  const api = useAxios();
  const [open, setOpen] = useState(false);

  const [users, setUsers] = useState([]);
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const { projectId } = useParams();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const location = useLocation();
  const [adminStatus, setAdminStatus] = useState({});

  const handleOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const fetchData = async () => {
    try {
      if (!projectId) {
        console.warn("No valid projectId provided, skipping fetch.");
        return; // Exit early if no valid projectId
      }

      // Fetch project details
      const response = await api.get(`/projects/${projectId}/`);
      const projectData = response.data;

      setSelectedUsers(projectData.users || []);

      // Fetch all users
      const usersResponse = await api.get("/users/");
      setUsers(usersResponse.data || []);

      // Initialize admin status for selected users
      const initialAdminStatus = (projectData.users || []).reduce(
        (acc, user) => {
          acc[user.id] = !!user.isAdmin;
          return acc;
        },
        {}
      );
      setAdminStatus(initialAdminStatus);
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [location.pathname]);

  const handleAdminCheckboxChange = (userId) => {
    setAdminStatus((prevStatus) => ({
      ...prevStatus,
      [userId]: !prevStatus[userId],
    }));
    setAdmins((prevStatus) => ({
      ...prevStatus,
      [userId]: !prevStatus[userId],
    }));
  };

  const handleAddMembers = async () => {
    try {
      // Prepare user ids from selectedUsers array
      const user_ids = selectedUsers.map((user) => user.id);

      // Send request to update project with selected users
      await api.patch(`/projects/${projectId}/`, { users: user_ids });

      // After adding members, proceed to assign admins
      // await handleAssignAdmin(user_ids);

      // Close modal after successful update
      handleClose();
    } catch (error) {
      console.error("Error adding members:", error);
    }
  };

  const handleAssignAdmin = async (user_ids) => {
    try {
      // Loop through each userId to assign admin
      for (const userId of user_ids) {
        await api.post(`/projects/${projectId}/assign-admin/${userId}/`, {
          users: [userId],
        });
      }

      // Optionally handle success logic here
    } catch (error) {
      console.error("Error assigning admins:", error);
    }
  };
  const [admins, setAdmins] = useState([]);

  return (
    <React.Fragment>
      <Button
        variant="contained"
        sx={{ backgroundColor: "#007DAB" }}
        onClick={handleOpen}
      >
        <PersonAdd /> Add members
      </Button>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="child-modal-title"
        aria-describedby="child-modal-description"
      >
        <Box sx={style}>
          {/* Back Button */}
          <IconButton
            sx={{ position: "absolute", top: 12, left: 12, color: "#007dab" }}
            onClick={handleClose}
          >
            <ArrowBack />
          </IconButton>

          {/* Title */}
          <Typography variant="h5" fontWeight="bold" sx={{ mb: 1 }}>
            Add Members
          </Typography>

          {/* Description */}
          <Typography variant="body1" sx={{ mb: 1 }}>
            Add members to this project.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Owners:</strong> Full control of projects and permissions.
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            <strong>Members:</strong> Can edit and view project content.
          </Typography>

          {/* Autocomplete */}
          <Autocomplete
            limitTags={3}
            multiple
            disableCloseOnSelect
            id="users"
            options={users}
            value={selectedUsers}
            onChange={(event, newValue) => setSelectedUsers(newValue)}
            getOptionLabel={(option) =>
              `${option.first_name} ${option.last_name}`
            }
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderOption={(props, option, { selected }) => {
              const isChecked =
                selected || selectedUsers.some((u) => u.id === option.id);
              return (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    sx={{ mr: 1 }}
                    checked={isChecked}
                  />
                  {option.first_name} {option.last_name}
                </li>
              );
            }}
            sx={{ width: "100%" }}
            renderInput={(params) => (
              <TextField {...params} label="Select users" />
            )}
          />

          {/* Admin Header */}
          {selectedUsers.length > 0 && (
            <Typography
              variant="h6"
              sx={{
                mt: 3,
                mb: 1,
                borderBottom: "1px solid #ddd",
                pb: 1,
              }}
            >
              Admin Assignment
            </Typography>
          )}

          {/* Selected Users List */}
          <List dense disablePadding>
            {selectedUsers.map((user, index) => (
              <ListItem
                key={user.id}
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 0,
                  py: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Typography>{index + 1}.</Typography>
                  <Avatar>
                    {user.first_name.charAt(0)}
                    {user.last_name.charAt(0)}
                  </Avatar>
                  <Typography>
                    {user.first_name} {user.last_name}
                  </Typography>
                </Box>
                <Checkbox
                  checked={adminStatus[user.id] || false}
                  onChange={() => handleAdminCheckboxChange(user.id)}
                />
              </ListItem>
            ))}
          </List>

          {/* Action Buttons */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 4,
            }}
          >
            <Button
              onClick={handleClose}
              variant="outlined"
              sx={{ color: "gray", borderColor: "gray" }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              sx={{ backgroundColor: "#007DAB" }}
              onClick={handleAddMembers}
            >
              Save
            </Button>
          </Box>
        </Box>
      </Modal>
    </React.Fragment>
  );
}

const PersistentDrawerLeft = () => {
  const api = useAxios();
  const token = localStorage.getItem("authTokens");
  const { logoutUser } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [members, setMembers] = useState([]);
  const [userId, setUserId] = useState(null);
  const [project, setProject] = useState([]);
  const [notificationCount, setNotificationCount] = useState(0);
  const [newNotification, setNewNotification] = useState(null);

  const handleNewNotification = (message) => {
    // Handle the notification message (e.g., display it, log it, etc.)
    console.log("New notification:", message);
    setNotificationCount((prevCount) => prevCount + 1); // Increment the badge count
  };

  if (token) {
    const decode = jwtDecode(token);
    var first_name = decode.first_name;
    var last_name = decode.last_name;
  }

  useEffect(() => {
    if (token) {
      const decode = jwtDecode(token);
      setUserId(decode.id);
    }
  }, [token]);

  const isMatchingUrl = () => {
    const { pathname } = location;

    // Matches: /sites/{siteId}/project-dashboard/{projectId}/open
    const projectDashboardPattern =
      /^\/sites\/[^/]+\/project-dashboard\/[^/]+\/open\/?$/;

    // Matches: /sites/{siteId}/project-dashboard/{projectId}/open/deliverables
    const deliverablesPattern =
      /^\/sites\/[^/]+\/project-dashboard\/[^/]+\/open\/deliverables\/?$/;

    return (
      projectDashboardPattern.test(pathname) ||
      deliverablesPattern.test(pathname)
    );
  };

  const [searchClicked, setSearchClicked] = useState(false);

  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [toggle, setToggle] = useState("light");
  const location = useLocation();
  const logisticsPath = buildContextualLogisticsPath(location.pathname);
  const { breadcrumbs, selectedItems } = useBreadcrumbs();
  const routeSiteId = location.pathname.match(/^\/sites\/([^/]+)/)?.[1];
  const routePlantId = location.pathname.match(/\/plants\/([^/]+)/)?.[1];
  const readinessSiteId =
    routeSiteId || selectedItems?.site?.id || breadcrumbs?.site?.id;
  const readinessPlantId =
    routePlantId || selectedItems?.plant?.id || breadcrumbs?.plant?.id;
  const readinessPath = readinessSiteId
    ? `/sites/${readinessSiteId}/readiness${readinessPlantId ? `?plant=${readinessPlantId}` : ""}`
    : "/readiness";

  // ── Design & Engineering Docs (GlobalDocuments) context: start ──────────
  // GlobalDocuments lives at a fixed "/design-engineering-docs" URL (no
  // route params of its own), so the only way to hand it context from
  // wherever the user currently is — Sites/Plants/Equipment/PanelComponents/
  // Instruments — is via a query string it reads on load. Site/Plant/
  // Equipment map onto GlobalDocuments' own {site, plant, equipment}
  // filters; Tier and Component have no matching filter, so their tag
  // number is passed as a search term instead.
  const routeAssetId = location.pathname.match(/\/assets\/([^/]+)/)?.[1];
  const routeSubAssetId = location.pathname.match(/\/sub-asset\/([^/]+)/)?.[1];
  const routeComponentId = location.pathname.match(/\/components\/([^/]+)/)?.[1];

  const docsSiteId =
    routeSiteId || selectedItems?.site?.id || breadcrumbs?.site?.id;
  const docsPlantId =
    routePlantId || selectedItems?.plant?.id || breadcrumbs?.plant?.id;
  const docsEquipmentId =
    routeAssetId || selectedItems?.asset?.id || breadcrumbs?.asset?.id;

  // Only pull a tag into the search box when we're actually on a route deep
  // enough to have one, so a stale breadcrumb left over from an earlier
  // drill-down doesn't silently leak a search term in on Sites/Plants/
  // Equipment. Component (deepest) wins over Tier when both are present.
  const docsTag = routeComponentId
    ? selectedItems?.component?.name || breadcrumbs?.component?.name || ""
    : routeSubAssetId
      ? selectedItems?.subAsset?.name || breadcrumbs?.subAsset?.name || ""
      : "";

  const docsParams = new URLSearchParams();
  if (docsTag) {
    // Tier/Component level: GlobalDocuments has no matching filter for
    // these, and its site/plant/equipment filters AND with search
    // server-side (DeliverableView.get_queryset chains separate .filter()
    // calls) — so also scoping to equipmentId here would silently exclude
    // any document that's linked to the tier/component but not directly to
    // the equipment. The search clause already matches across every link
    // level (site/plant/equipment/tier/component/instrument) on its own,
    // so the tag alone is enough.
    docsParams.set("search", docsTag);
  } else {
    if (docsSiteId) docsParams.set("siteId", docsSiteId);
    if (docsPlantId) docsParams.set("plantId", docsPlantId);
    if (docsEquipmentId) docsParams.set("equipmentId", docsEquipmentId);
  }
  const docsQuery = docsParams.toString();
  const docsPath = docsQuery
    ? `/design-engineering-docs?${docsQuery}`
    : "/design-engineering-docs";
  // ── Design & Engineering Docs (GlobalDocuments) context: end ────────────
  let { projectId } = useParams();

  const [openModal, setModalOpen] = useState(false);
  const handleOpen = () => setModalOpen(true);
  const handleClose = () => setModalOpen(false);

  useEffect(() => {
    // Function to handle window resize event
    const handleResize = () => {
      setSearchClicked(false); // Reset search icon click state when screen size changes
    };

    // Add event listener for window resize
    window.addEventListener("resize", handleResize);

    // Remove event listener on component unmount
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const handleDarkModeChange = () => {
    if (darkMode && toggle) {
      setToggle((theme.palette.mode = "light"));
      setDarkMode(false);
    } else {
      setToggle((theme.palette.mode = "dark"));
      setDarkMode(true);
    }

    // Your dark mode change logic here
  };

  const list = [
    {
      name: "Home",
      url: "/",
      icon: Home,
    },
    {
      name: "Instrument index",
      url: "/",
      icon: Description,
    },
    {
      name: "DataSheets",
      url: "/",
      icon: ViewList,
    },
    {
      name: "Packages",
      url: "/",
      icon: Assignment,
    },
    {
      name: "Vendor Docs",
      url: "/",
      icon: CreditCardIcon,
    },
    {
      name: "Reports",
      url: "/",
      icon: Assessment,
    },
    {
      name: "Administration",
      url: "/",
      icon: SettingsIcon,
    },
    {
      name: "Suppliers",
      url: "/",
      icon: Category,
    },
    {
      name: "Recycle Bin",
      url: "/recyclebin",
      icon: DeleteIcon,
    },
  ];

  const handleDrawerClose = () => {
    setOpen(false);
  };
  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const isMenuOpen = Boolean(anchorEl);

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuId = "primary-search-account-menu";
  const renderMenu = (
    <Menu
      anchorEl={anchorEl}
      id={menuId}
      keepMounted
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
          mt: 1.5,
          "& .MuiAvatar-root": {
            width: 72,
            height: 72,
            color: "black",
            fontSize: "h4.fontSize",
            border: 1,
            ml: -0.5,
            mr: 1,
          },
          "&::before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      open={isMenuOpen}
      onClose={handleMenuClose}
    >
      <MenuItem onClick={handleMenuClose}>
        <Avatar>
          {first_name.charAt(0)}
          {last_name.charAt(0)}
        </Avatar>
      </MenuItem>
      <MenuItem sx={{ fontWeight: "bold", color: "secondary.main" }} onClick={handleMenuClose}>
        {first_name} {last_name}
      </MenuItem>
      <Divider />
      <MenuItem component={Link} onClick={handleMenuClose} to="/myprofile">
        <ListItemIcon>
          <Settings fontSize="small" />
        </ListItemIcon>
        Settings
      </MenuItem>
      <MenuItem onClick={() => logoutUser(false)}>
        <ListItemIcon>
          <Logout fontSize="small" />
        </ListItemIcon>
        Logout
      </MenuItem>
    </Menu>
  );

  const navLinks = [
    { label: "Design & Engineering Docs", path: docsPath, icon: DesignServicesIcon },
    { label: "PlantReadiness", path: readinessPath, icon: FactCheckOutlined },
    { label: "Faults/Diagnostics", path: "/faults-diagnostics", icon: WarningAmberRounded },
    { label: "Logistics", path: logisticsPath, icon: LocalShippingIcon },
    { label: "MaintenanceGuide", path: "/maintenance-guide", icon: MenuBookOutlinedIcon },
  ];

  return (
    <Box sx={{ display: "flex", overflow: "hidden" }}>
      <CssBaseline />
      <AppBar
        elevation={3}
        position="fixed"
        open={open}
        sx={{
          backgroundColor: "secondary.main",
          zIndex: 1000, // Ensure it's above the drawer
        }}
      >
        <Toolbar>

          <NavLink
            to={"/"}
            className="no-underline text-white"
            style={{ flexShrink: 0 }}
          >
            <motion.img
              src={logo}
              alt="logo"
              className="h-20 w-auto bg-transparent object-contain"
              initial={{ opacity: 0, scale: 0.8 }} // Start smaller and transparent
              animate={{ opacity: 1, scale: 1 }} // Animate to normal size
              transition={{ duration: 0.6, ease: "easeOut" }} // Smooth transition
              whileHover={{
                scale: 1.5, // Magnify on hover
                transition: { duration: 0.3 },
              }}
              style={{ originX: 0.5, originY: 0.5 }} // Scale from center so it stays in place
            />
          </NavLink>

          {/* Tablet and desktop: the links take the space between the logo and the icons; the
              ones that do not fit move into a "More" menu (see HeaderNavLinks). */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, flex: "1 1 0", minWidth: 0, pl: 2 }}>
            <HeaderNavLinks links={navLinks} />
          </Box>

          {/* Kept mounted at every size (hidden on phones): the notifications socket lives here. */}
          <Box sx={{ display: { xs: "none", sm: "flex" }, alignItems: "center", flexShrink: 0 }}>

            {/* ── Divider between nav links and icons ── */}
            <Divider
              orientation="vertical"
              flexItem
              sx={{ mx: 1, borderColor: "rgba(255,255,255,0.3)" }}
            />
            { /*End of Desktop Navigation Links*/}

            <Tooltip title={`Messages`}>
              {/* ... rest of your existing icon buttons unchanged ... */}
              <IconButton
                size="large"
                aria-label="show 4 new mails"
                color="inherit"
              >
                <Badge badgeContent={0} color="error">
                  <MailIcon />
                </Badge>
              </IconButton>
            </Tooltip>


            {/* Your original Tooltip and IconButton */}
            <Tooltip title={`Notifications`}>
              <IconButton
                size="large"
                aria-label={`show ${notificationCount} new notifications`}
                color="inherit"
              >

                <Badge badgeContent={notificationCount} color="error">
                  <>
                    {/* Render other navigation components */}

                    {/* WebSocketNotification Component */}
                    <WebSocketNotification
                      onNewNotification={handleNewNotification}
                    />

                    {/* Optionally display the new notification elsewhere in the UI */}
                    {newNotification && (
                      <div>
                        <p>Latest Notification: {newNotification}</p>
                      </div>
                    )}
                  </>
                </Badge>
              </IconButton>
            </Tooltip>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls={menuId}
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
            >
              <Tooltip title={`${first_name} ${last_name}`}>
                <Avatar>
                  {first_name.charAt(0)}
                  {last_name.charAt(0)}
                </Avatar>
              </Tooltip>
            </IconButton>
          </Box>

          {/* Phone: everything is behind one menu button. */}
          <Box sx={{ display: { xs: "flex", sm: "none" }, flex: 1, justifyContent: "flex-end" }}>
            <HeaderMobileMenu
              links={navLinks}
              userName={`${first_name || ""} ${last_name || ""}`.trim()}
              initials={`${first_name?.charAt(0) || ""}${last_name?.charAt(0) || ""}`}
              notificationCount={notificationCount}
              onLogout={() => logoutUser(false)}
            />
          </Box>
        </Toolbar>
      </AppBar>

      {renderMenu}

      <Drawer
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          transition: "width 0.3s ease",
          "& .MuiDrawer-paper": {
            width: open ? drawerWidth : 0,
            transition: "width 0.3s ease",
            overflowX: "hidden", // prevent horizontal scrollbars
            boxSizing: "border-box",
          },
        }}
        variant="persistent"
        anchor="left"
        open={open}
      >
        <DrawerHeader>
          <IconButton onClick={handleDrawerClose}>
            {theme.direction === "ltr" ? (
              <ChevronLeftIcon />
            ) : (
              <ChevronRightIcon />
            )}
          </IconButton>
        </DrawerHeader>
        <Divider />
        <List
          component={Stack}
          alignItems="center"
          justifyContent="center"
          disablePadding
        >
          {list.map((item, index) => (
            <Tooltip title={item.name} key={index}>
              <ListItem disablePadding sx={{ width: "80%" }} disableGutters>
                <Link
                  to={`${item.url}`}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <ListItemButton centerRipple>
                    <ListItemIcon>
                      <item.icon />
                    </ListItemIcon>
                    <ListItemText primary={item.name} />
                  </ListItemButton>
                </Link>
              </ListItem>
            </Tooltip>
          ))}
        </List>
        <Divider />
      </Drawer>
      {/* Breadcrumbs bar — fixed directly below the AppBar */}
      <Box
        sx={{
          position: "fixed",
          top: { xs: "56px", sm: "74px" },
          left: open ? drawerWidth : 0,
          right: 0,
          zIndex: 500,
          backgroundColor: "background.paper",
          borderBottom: "1px solid",
          borderColor: "divider",
          px: 0.5,
          transition: "left 0.3s ease",
        }}
      >
        <BasicBreadcrumbs />
      </Box>

      {/* Spacer so page content starts below both the AppBar and breadcrumb bar */}
      <Box sx={{ width: "100%" }}>
        <DrawerHeader />
        <Box sx={{ height: 40 }} /> {/* matches the breadcrumb bar height */}
      </Box>
      <Modal
        open={openModal}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          {/* Header */}
          <Typography
            id="modal-modal-title"
            variant="h5"
            fontWeight="bold"
            gutterBottom
          >
            Project Membership
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            {members.length} member{members.length !== 1 ? "s" : ""}
          </Typography>

          {/* Optional action/modal */}
          <ChildModal />

          {/* Members List */}
          <List dense>
            {members.map((member, index) => (
              <ListItem key={index} divider>
                <ListItemAvatar>
                  <Avatar sx={{ bgcolor: "#007dab" }}>
                    <Person />
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={`${member.first_name} ${member.last_name}`}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      </Modal>
    </Box>
  );
};

export default PersistentDrawerLeft;